import { GameState, gameStates, getGameStateById } from './machine/state-types';
import { trackEvent, setCasinoId, initAnalytics } from '@src/utils/init-analytics';
import dayjs from 'dayjs';
import { ASSETS } from '@src/constants';
import { GameConfig, GameInitData, PlayResponse } from '@src/types';
import { allowToBet, currency, gameRoundInfo, gameStatus, unformattedBalance, videosFiltered } from '@src/store';
import { notifications } from '@src/store/notifications';
import { getNormalizeNumberWithDecimals, getUrlParam, userAgentInfo } from '@src/utils/global';
import { abTestParams, exitUrl, gameInitParams, gameRTPValue, hideHomeButton } from '@src/store/misc';
import { initBetsValues } from '@src/store/betting';
import { Howl } from 'howler';
import { appStarted } from '@src/store/ui';
import { RequestError } from '@src/backend/types';
import { stopAutoBet } from '@src/store';
import { backend } from '../backend';
import { betColorLimits, chipPrice, initChipValues } from '../store/chips';
import { resetGameState } from './machine/helpers';
import { initHistory, setMaxLimits } from '../betting/betting';
import { get } from 'svelte/store';

function handleInitError(error: RequestError) {
    if (error.response) {
        // Request made but the server responded with an error

        console.error(error.response.data);
        console.error(error.response.status);
        console.error(error.response.headers);

        notifications.showError({
            title: error.response.data.message
        });

        stopAutoBet();
        resetGameState();
    } else if (error.request) {
        // Request made but no response is received from the server
        console.log(error.request);

        notifications.showError({
            title: 'Server Error'
        });

        gameStatus.set('DISCONNECTED');
    } else {
        console.error('Init request error', error.message);
    }
}

function transformGameConfigToArray(gameConfig: GameConfig): number[] {
    const order = [
        'limit_bets_1',
        'limit_bets_2',
        'limit_bets_5',
        'limit_bets_10',
        'limit_bets_ball_spin',
        'limit_bets_plinko',
        'limit_bets_cash_reels',
        'limit_bets_fortune_wheel'
    ];

    return order.map(key => gameConfig[key as keyof GameConfig]);
}

function handleInitRequestSuccess(init: GameInitData) {
    const configData = init.config;
    const customParams = init?.custom_params;
    const { exponent, subunits, symbol } = init.currency_attributes;
    const chipPrice = init.custom_params.chip_price;

    const normalizeNumbers = (number: number | number[]): string | string[] => {
        if (Array.isArray(number)) {
            return number.map(value => getNormalizeNumberWithDecimals(value, exponent, subunits));
        }

        return getNormalizeNumberWithDecimals(number, exponent, subunits);
    };

    const maxBets = transformGameConfigToArray(customParams.game_config);
    for (let i = 0; i < maxBets.length; i++) maxBets[i] = maxBets[i] / chipPrice;

    setMaxLimits(maxBets);
    betColorLimits.set(maxBets);
    exitUrl.set(customParams?.return_url);
    hideHomeButton.set(configData?.hide_home_button);
    abTestParams.set(customParams?.ab);

    setCasinoId(configData?.casino_id);

    trackEvent('start_iframe', {
        balance: normalizeNumbers(init.balance),
        browser_info: userAgentInfo
    });

    gameInitParams.set(init);
    currency.set(symbol);
    allowToBet.set(true);
    gameRTPValue.set(configData?.rtp);
    unformattedBalance.set(init.balance);

    initHistory();

    initBetsValues({
        bet_limits: normalizeNumbers(configData.bet_limits),
        default_bet: normalizeNumbers(configData.default_bet),
        popular_bet: normalizeNumbers(configData.popular_bet)
    });
}

async function preloadAssets(data: GameInitData) {
    const isVideo = asset => /\.(mp4)$/gi.test(asset.url);
    const videos = ASSETS.filter(isVideo);
    const durationStartLoading = dayjs();
    const customParams = data?.custom_params;
    const abTest = customParams?.ab;

    window.__assets_cache = {};

    await Promise.all(
        ASSETS.map(
            asset =>
                new Promise<void>(resolve => {
                    if (asset.type === 'audio/mp3') {
                        return new Howl({
                            src: [asset.url],
                            preload: true,
                            onload: function () {
                                window.__assets_cache[asset.name] = this;
                                resolve();
                            }
                        });
                    }

                    return resolve();
                })
        )
    );

    const duration = dayjs.duration(dayjs().diff(durationStartLoading)).asSeconds();
    console.log('assets loading duration ' + duration);

    // Preload all videos in array
    async function preloadVideos(videos) {
        const filteredVideos = videos.filter(video => {
            return abTest?.video === '3d' ? video.name.match(/^((?!real).)*$/) : video.name.match('real');
        });

        videosFiltered.set(filteredVideos);

        for (const video of filteredVideos) {
            const videoUrl = video.url;

            let vidElement = document.createElement('video');

            vidElement.src = videoUrl;
            vidElement.preload = 'auto';

            vidElement.load();

            await new Promise(resolve => {
                vidElement.onloadeddata = resolve;
            });

            // Remove element from memory
            vidElement = null;
        }
    }

    void preloadVideos(videos); // TODO: maybe await here (in legacy code it was void)
}

const secretToken = getUrlParam('secret');

export const state: GameState = {
    name: gameStates.init,
    async onEnter(setState) {
        // if (!getUserTrackId())
        await initAnalytics(); //Always init analytics before the game init
        const initResult = await backend.init(secretToken).catch(error => handleInitError(error)); // pass token here
        if (!initResult) {
            //TODO collect error stats/reload the game
            throw new Error('init result missing');
        }

        handleInitRequestSuccess(initResult);
        initChipValues(initResult?.custom_params);
        await preloadAssets(initResult);
        appStarted.set(true);

        const bonusGame = initResult.state?.resp?.bonus_game;

        if (bonusGame) {
            //Restore previous play response from init data
            const playResponse: PlayResponse = {
                round: initResult.state.round,
                step: null,
                balance: initResult.balance,
                resp: initResult.state.resp,
                final: initResult.state.final
            };
            gameRoundInfo.set(playResponse);
            setState(getGameStateById(bonusGame));
        } else {
            setState(gameStates.game);
        }

        // Run game fist, analytics later
        // initAnalytics();
    },
    onExit() {}
};
